import React, { useContext, useState } from 'react';

import AccountContext from 'contexts/AccountContext';
import BoardContext from 'contexts/BoardContext';
import FeatureGatesContext from 'contexts/FeatureGatesContext';
import Button from 'kingpin/atoms/Button';
import ContextMenu, { Option } from 'kingpin/atoms/ContextMenu';
import Tooltip from 'components/Tooltip';
import { BonusOverviewContext } from 'components/DriverAX/providers/BonusOverviewProvider';
import ViewDriverAppModal from 'components/DriverAX/screens/DriverBonusOverview/ViewDriverAppModal';
import {
  DRIVER_APP_EMBED_ORIGIN,
  DRIVER_APP_EMBED_PREVIEW_ORIGIN,
} from 'components/DriverAX/screens/BonusAppEmbed';
import usePopup from 'hooks/usePopup';
import isPerformanceBoard from 'isPerformanceBoard';

const ViewDriverAppButton = () => {
  const { driverAppEmbedCarrierSlug, driverAppEmbedProgramSlug } =
    useContext(AccountContext);
  const { isDriverAppEmbedPreviewEnabled } = useContext(FeatureGatesContext);
  const { bonusOverview } = useContext(BonusOverviewContext);
  const { board } = useContext(BoardContext);
  const { isOpen, open, close } = usePopup();
  const [origin, setOrigin] = useState<string>(DRIVER_APP_EMBED_ORIGIN);

  const openLive = () => {
    setOrigin(DRIVER_APP_EMBED_ORIGIN);
    open();
  };
  const openPreview = () => {
    setOrigin(DRIVER_APP_EMBED_PREVIEW_ORIGIN);
    open();
  };

  const driverAppEmbed = isPerformanceBoard(board)
    ? board.driverAppEmbed
    : undefined;

  // Opt-in: the button only shows when explicitly enabled on the board. Slugs
  // fall back to the account-level Advanced settings when not overridden.
  const isButtonEnabled = driverAppEmbed?.isButtonEnabled === true;
  const effectiveCarrierSlug =
    driverAppEmbed?.carrierSlug || driverAppEmbedCarrierSlug;
  const effectiveProgramSlug =
    driverAppEmbed?.programSlug || driverAppEmbedProgramSlug;

  if (!isButtonEnabled || !effectiveCarrierSlug) {
    return null;
  }

  // With the preview gate on (and bonus data loaded), the phone button opens a
  // Live/Preview menu. Otherwise it behaves as before — a single click opens
  // the live embed, and stays disabled until the bonus overview has loaded.
  const showMenu = isDriverAppEmbedPreviewEnabled && !!bonusOverview;

  return (
    <>
      {showMenu ? (
        <ContextMenu
          icon={'phone'}
          buttonType={'Secondary'}
          tooltip={'View Driver App'}
          testId={'view-driver-app-slideout'}
        >
          <Option label={'Live'} onClick={openLive} />
          <Option label={'Preview'} onClick={openPreview} />
        </ContextMenu>
      ) : (
        <Tooltip content="View Driver App">
          <Button
            size={'Medium'}
            type={'Secondary'}
            icon={'phone'}
            onClick={openLive}
            isDisabled={!bonusOverview}
            testId={'view-driver-app-slideout'}
          />
        </Tooltip>
      )}
      <ViewDriverAppModal
        isOpen={isOpen}
        close={close}
        origin={origin}
        carrierSlug={effectiveCarrierSlug}
        programSlug={effectiveProgramSlug}
      />
    </>
  );
};

export default ViewDriverAppButton;
